import { TimetableDay } from '@versita/shared/models';
import { filter, map, mapObjIndexed, omit, prop, range } from 'ramda';
import { FC, useState } from 'react';
import {
  CompleteTimetableItemProps,
  TimetableItem,
  TimetableItemProps,
} from '../../atoms/TimetableItem';
import {
  StyledTimetable,
  StyledTimetableCell,
  StyledTimetableDay,
  StyledTimetableDayLabel,
  StyledTimetableHeader,
  StyledTimetableHeaderControl,
  StyledTimetableHeaderControls,
  StyledTimetableHeaderLabel,
  StyledTimetableItemsWrapper,
  StyledTimetableRoot,
  StyledTimetableTime,
  StyledTimetableTimes,
  StyledTimetableWrapper,
} from './Timetable.styles';
import { ChevronLeft, ChevronRight } from '../../icons';
import { Radio } from '../../atoms/Radio';
import { ButtonSize } from '../../atoms/Button';
import { TimetableEventInfo } from '../../molecules/TimetableEventInfo';
import { useItemPositions } from './hooks/useItemPositions';

type TimetableViewMode = 'WEEK' | 'DAY';

export interface TimetableProps {
  /** whether the timetable events are still loading */
  loading?: boolean;
  /** the days to show in the timetable, usually either a week or a single day */
  days: TimetableDay[];
  /** the label for the current period (week or day) e.g. "Week 1", "April 23-29th 2021" or "April 23rd" */
  periodLabel: string;
  /** the events to show in the timetable */
  events: Record<string, TimetableItemProps[]>;
  /** the current view mode */
  viewMode: TimetableViewMode;
  /** the start hour for the timetable, in 24-hour format (e.g. 9) */
  rangeStart?: number;
  /** the end hour for the timetable, in 24-hour format (e.g. 17) */
  rangeEnd?: number;
  /** callback for when the period should be incremented */
  onIncrementPeriod: () => void;
  /** callback for when the period should be decremented */
  onDecrementPeriod: () => void;
  /** callback for when the view mode is changed, if none is provided, the view mode buttons will not be rendered */
  onChangeViewMode?: (viewMode: TimetableViewMode) => void;
  /** callback to determine whether to show or hide an item, called each render */
  itemVisibility?: (item: TimetableItemProps) => boolean;
  timezone: string;
}

/**
 * A schedule/timetable view with week and day variants
 *
 * The days can be generated using the `getTimetableWeekDays` helper function
 * which is compatible with the format expected by this component
 *
 * TODO: both this and <Calendar> shouldn't really expect the parent component
 * to have to generate the days array. Instead, this component should take the
 * start date and run getTimetableWeekDays itself.
 * The same goes for the viewmode. It's strange the the view mode isn't very
 * semantically-utilised by this component - e.g. it just renders the amount of
 * days provided regardless of the view mode
 */
export const Timetable: FC<TimetableProps> = ({
  loading,
  days,
  events,
  onDecrementPeriod,
  onIncrementPeriod,
  periodLabel,
  onChangeViewMode,
  viewMode = 'WEEK',
  itemVisibility = () => true,
  rangeStart = 8,
  rangeEnd = 20,
}) => {
  const filteredEvents = mapObjIndexed(filter(itemVisibility), events);
  const timetableProps = useItemPositions({
    events: filteredEvents,
    rangeStart,
    rangeEnd,
  });

  const [timetableModal, setTimetableModal] = useState<
    TimetableItemProps[] | []
  >([]);
  const showTimetableModal = (events: TimetableItemProps[]) => () => {
    setTimetableModal(events);
  };
  return (
    <StyledTimetableRoot>
      <StyledTimetableHeader>
        <StyledTimetableHeaderControls>
          <StyledTimetableHeaderControl
            data-testid={`previous-${viewMode.toLowerCase()}-control`}
            onClick={onDecrementPeriod}
            title={`Go to previous ${viewMode.toLowerCase()}`}
          >
            <ChevronLeft />
          </StyledTimetableHeaderControl>
          <StyledTimetableHeaderLabel data-testid="period-label">
            {periodLabel}
          </StyledTimetableHeaderLabel>
          <StyledTimetableHeaderControl
            onClick={onIncrementPeriod}
            data-testid={`next-${viewMode.toLowerCase()}-control`}
            title={`Go to next ${viewMode.toLowerCase()}`}
          >
            <ChevronRight />
          </StyledTimetableHeaderControl>
        </StyledTimetableHeaderControls>
        {onChangeViewMode && (
          <Radio
            data-testid="view-mode-radio"
            legend="View Mode"
            legendVisible={false}
            $buttonSize={ButtonSize.Small}
            items={[
              { id: 'week', label: 'Week', value: 'WEEK' },
              { id: 'day', label: 'Day', value: 'DAY' },
            ]}
            name="view-mode"
            value={viewMode}
            inputProps={{
              onChange: (e) =>
                onChangeViewMode(e.currentTarget.value as TimetableViewMode),
            }}
          />
        )}
      </StyledTimetableHeader>
      <StyledTimetable>
        <StyledTimetableTimes>
          {range(rangeStart, rangeEnd + 1).map((hour) => (
            <StyledTimetableTime key={hour}>
              {hour < 10 ? `0${hour}:00` : `${hour}:00`}
            </StyledTimetableTime>
          ))}
        </StyledTimetableTimes>
        <StyledTimetableWrapper $loading={loading} tabIndex={0}>
          {days.map((day, dayNumber) => (
            <StyledTimetableDay key={day.label} data-testid="timetable-day">
              <StyledTimetableDayLabel $current={day.current}>
                {day.label}
              </StyledTimetableDayLabel>
              <StyledTimetableCell
                $current={day.current}
                $dayNumber={dayNumber}
              />
              <StyledTimetableItemsWrapper>
                {!loading &&
                  timetableProps?.[day.weekDay]?.timetableEvents.map(
                    (event) => {
                      const overlapGroup = event.overlapGroupId
                        ? timetableProps?.[day.weekDay].overlapGroups[
                            event.overlapGroupId
                          ]
                        : null;
                      const subEvents = overlapGroup?.events || [];
                      return overlapGroup && viewMode === 'WEEK' ? (
                        event.horizontalIndex < 1 ? (
                          <TimetableItem
                            data-testid="timetable-item"
                            key={event.id}
                            onClick={showTimetableModal(subEvents)}
                            label={`View ${event.overlaps + 1} events`}
                            topPositionPercent={overlapGroup.topPositionPercent}
                            heightPercent={overlapGroup.heightPercent}
                            overlaps={0}
                            isMultiEvent={true}
                            subEvents={subEvents}
                            start={overlapGroup.start.toISOString()}
                            end={overlapGroup.end.toISOString()}
                            {...omit(
                              [
                                'overlaps',
                                'topPositionPercent',
                                'heightPercent',
                                'label',
                                'footer',
                                'start',
                                'end',
                              ],
                              event,
                            )}
                          />
                        ) : null
                      ) : (
                        <TimetableItem
                          data-testid="timetable-item"
                          key={event.id}
                          onClick={showTimetableModal([event])}
                          {...event}
                        />
                      );
                    },
                  )}
                {range(rangeStart, rangeEnd).map((hour) => (
                  <StyledTimetableCell
                    $current={day.current}
                    key={hour}
                    $dayNumber={dayNumber}
                  />
                ))}
              </StyledTimetableItemsWrapper>
              <StyledTimetableCell
                $current={day.current}
                $dayNumber={dayNumber}
              />
            </StyledTimetableDay>
          ))}
        </StyledTimetableWrapper>
      </StyledTimetable>
      {timetableModal.length ? (
        <TimetableEventInfo
          data-testid="timetable-event-modal"
          accessibleName="Timetable Event Information"
          open={!!timetableModal}
          onClose={() => setTimetableModal([])}
          events={timetableModal}
        />
      ) : null}
    </StyledTimetableRoot>
  );
};

export default Timetable;
